import { MapPin, Wifi } from "lucide-react";
import type { TheirStackJob } from "../api/TheirStackJobsApi";
import { daysAgo, formatPostedDate } from "./shared";
import CompanyApplyChip from "shared/CompanyApplyChip";
import RemoteScopeChip from "shared/RemoteScopeChip";
import TechTagChip from "shared/TechTagChip";

interface Props {
  job: TheirStackJob;
  onClick: () => void;
}

export default function JobCard({ job, onClick }: Props) {
  return (
    <div
      onClick={onClick}
      className={`bg-surface/60 border rounded-xl p-4 cursor-pointer hover:border-border transition-all select-none ${
        job.is_active ? "border-border/60" : "border-red-900/40 opacity-60"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Company initial */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          <div className="w-10 h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-center">
            <span className="text-sm font-bold text-primary font-inter">
              {job.company_name.charAt(0).toUpperCase()}
            </span>
          </div>
          {job.date_posted && (
            <span
              title={formatPostedDate(job.date_posted)}
              className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse"
            >
              {daysAgo(job.date_posted)}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {job.job_title}
            </h3>
            <div className="flex items-center gap-1 shrink-0">
              <CompanyApplyChip companyCanApply={job.company_can_apply} metadataJson={job.company_analysis_metadata} />
              {job.remote && (
                <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-inter">
                  <Wifi className="w-2.5 h-2.5" />
                  Remote
                </span>
              )}
              {job.hybrid && !job.remote && (
                <span className="text-[10px] px-1.5 py-0.5 rounded border bg-sky-500/10 text-sky-400 border-sky-500/20 font-inter">
                  Hybrid
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
            <span className="text-xs text-muted-foreground font-inter truncate">{job.company_name}</span>
            {job.location && (
              <>
                <span className="text-muted-foreground text-xs shrink-0">•</span>
                <span className="flex items-center gap-0.5 text-xs text-muted-foreground font-inter truncate shrink-0">
                  <MapPin className="w-3 h-3 shrink-0" />
                  {job.location}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {job.seniority && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
                {job.seniority}
              </span>
            )}
            {job.employment_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
                {job.employment_type}
              </span>
            )}
            {!job.is_active && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20 font-inter">
                Deactivated
              </span>
            )}
            <RemoteScopeChip
              remoteScope={job.remote_scope}
              analysisJson={job.llm_analysis_metadata}
              className="ml-auto"
            />
          </div>

          {/* Tech tag — flickering, placed last so it doesn't compete with regular metadata */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2">
            <TechTagChip label={job.stack_source} />
          </div>
        </div>
      </div>
    </div>
  );
}
